/* eslint-disable react/prop-types */
/**
 * One parent on the review board.
 *
 * Collapsed it is a summary; expanded it shows every child + category with its
 * own money, because "why is this parent 7,500 and not 10,000" is answered at
 * the line level. The two comment boxes save independently — a centre saving
 * its note must not overwrite an HQ reply written a second earlier, which is
 * why each button sends only its own field.
 */
import {
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import dayjs from 'dayjs'
import React, { useState } from 'react'

import * as styles from './styles'

const rupees = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`

const OUTCOME_TONE = {
  'WOULD BOOK': 'book',
  'WOULD TERMINATE': 'terminate',
  'ALREADY BOOKED': 'done',
  SKIPPED: 'muted',
  ERROR: 'terminate'
}

const REVIEW_STATUSES = [
  { value: 'pending', label: 'Not reviewed' },
  { value: 'no_action', label: 'No action needed' },
  { value: 'centre_flagged', label: 'Flagged' },
  { value: 'approved', label: 'Approved' },
  { value: 'hold', label: 'On hold' }
]

/* An ALREADY BOOKED or SKIPPED parent was priced by the dry run and then left
   alone, so its money is hypothetical — a "short 3,000 coins" on a parent who
   has already paid invents a problem that does not exist. Show the outcome and
   the reason for those, and nothing that looks like a bill. */
const DECIDED_OUTCOMES = ['ALREADY BOOKED', 'SKIPPED']

export const ParentRow = ({ row, onSave, isCommit }) => {
  const [open, setOpen] = useState(false)
  const [centreDraft, setCentreDraft] = useState(row.centre_comment || '')
  const [hqDraft, setHqDraft] = useState(row.hq_comment || '')
  const [saving, setSaving] = useState('')

  const save = async (field, value) => {
    setSaving(field)
    await onSave(row.uuid, { [field]: value })
    setSaving('')
  }

  const centreDirty = centreDraft !== (row.centre_comment || '')
  const hqDirty = hqDraft !== (row.hq_comment || '')
  const hasGst = Number(row.gst_amount || 0) > 0
  const settled = DECIDED_OUTCOMES.includes((row.outcome || '').toUpperCase())
  // Debit state belongs ONLY to a parent on the e-mandate rail, on a COMMIT
  // run. A coins parent has no mandate and is never debited, so telling one
  // "no debit was triggered" is noise on 608 rows. `payment_status` cannot be
  // the test — the serializer always sends it, empty on a dry run.
  const hasMandate = Boolean(row.mandate_method)
  const expectsDebit = isCommit && hasMandate && !settled

  return (
    <styles.Row $terminating={row.will_terminate}>
      <styles.Grid>
        <styles.Cell>
          <styles.ParentName>
            <IconButton size="small" onClick={() => setOpen((o) => !o)}>
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
            {/* Deep-links into the parent dashboard, which auto-selects the
                family from ?parent=<search text> (fetchOnMount +
                initialSelectIndex=0). New tab on purpose: a centre checking one
                family must not lose its place in a 900-row review. */}
            <styles.ParentLink
              href={`/home/parent?parent=${encodeURIComponent(
                row.parent_phone || row.parent_name
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this parent's dashboard in a new tab"
            >
              {row.parent_name}
            </styles.ParentLink>
          </styles.ParentName>
          <styles.Sub>{row.parent_phone}</styles.Sub>
          <styles.Sub>{row.centre_name}</styles.Sub>
          {hasMandate ? (
            <styles.Sub>
              autopay: {row.mandate_method}
              {row.mandate_cap_rupees
                ? ` · cap ${rupees(row.mandate_cap_rupees)}`
                : ''}
            </styles.Sub>
          ) : null}
        </styles.Cell>

        <styles.Cell>
          <styles.Summary>{row.children_summary || '—'}</styles.Summary>
          <styles.Sub>
            {row.enrolment_count} enrolment
            {row.enrolment_count === 1 ? '' : 's'} · {row.category_count}{' '}
            categor{row.category_count === 1 ? 'y' : 'ies'}
            {row.paused_category_count
              ? ` · ${row.paused_category_count} paused`
              : ''}
          </styles.Sub>
        </styles.Cell>

        <styles.Cell>
          <styles.Outcome $tone={OUTCOME_TONE[row.outcome] || 'muted'}>
            {row.outcome || '—'}
          </styles.Outcome>
          {row.already_booked ? (
            <Chip size="small" label="already booked" />
          ) : null}
          {row.reason ? (
            <styles.Sub title={row.reason}>{row.reason}</styles.Sub>
          ) : null}
          {row.coins_short_by > 0 && !settled ? (
            <styles.Danger>short {row.coins_short_by} coins</styles.Danger>
          ) : null}
          {/* On a COMMIT run this is the only thing anyone wants to know: was
              the debit actually triggered, and is it still only "created"?
              A UPI autopay sits at created for a day or so before the bank
              settles it, so "created" is normal, not a failure. */}
          {row.payment_id ? (
            <styles.Charged>
              debit triggered · {row.payment_status || 'created'}
              <styles.Sub>{row.payment_id}</styles.Sub>
            </styles.Charged>
          ) : null}
          {expectsDebit && !row.payment_id ? (
            <styles.Danger>no debit was triggered</styles.Danger>
          ) : null}
        </styles.Cell>

        <styles.Cell>
          {settled ? (
            /* Nothing will be charged for this parent, so no figure here is a
               bill. Showing the ladder anyway made an ALREADY BOOKED row read
               as "short 3,000 coins" on a family that had already paid. */
            <styles.Settled>
              Nothing to charge — this parent was left untouched by the run.
            </styles.Settled>
          ) : (
            <>
              <styles.MoneyLine>
                <span>before discount</span>
                <b>{rupees(row.cost_before_discount)}</b>
              </styles.MoneyLine>
              <styles.MoneyLine>
                <span>discount ({Number(row.discount_percentage || 0)}%)</span>
                <b>−{rupees(row.discount_value)}</b>
              </styles.MoneyLine>
              <styles.MoneyLine>
                <span>after discount</span>
                <b>{rupees(row.cost_after_discount)}</b>
              </styles.MoneyLine>
              {hasGst ? (
                <styles.MoneyLine>
                  <span>GST 18%</span>
                  <b>{rupees(row.gst_amount)}</b>
                </styles.MoneyLine>
              ) : null}
              <styles.MoneyLine $total>
                <span>{hasGst ? 'to debit' : 'coins to deduct'}</span>
                <b>
                  {hasGst
                    ? rupees(row.total_payable)
                    : `${Number(row.coins_to_deduct || 0).toLocaleString(
                        'en-IN'
                      )} coins`}
                </b>
              </styles.MoneyLine>
              <styles.Why>{row.discount_explanation}</styles.Why>
              <styles.Sub>
                wallet before:{' '}
                {Number(row.coin_balance_before || 0).toLocaleString('en-IN')}
              </styles.Sub>
            </>
          )}
        </styles.Cell>

        <styles.Cell>
          <TextField
            multiline
            minRows={2}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="What does the centre know about this family?"
            value={centreDraft}
            onChange={(e) => setCentreDraft(e.target.value)}
          />
          <styles.CommentFoot>
            {row.centre_comment_by_name ? (
              <span>
                {row.centre_comment_by_name}
                {row.centre_comment_at
                  ? ` · ${dayjs(row.centre_comment_at).format('D MMM, HH:mm')}`
                  : ''}
              </span>
            ) : (
              <span />
            )}
            <Button
              size="small"
              disabled={!centreDirty || saving === 'centre_comment'}
              onClick={() => save('centre_comment', centreDraft)}
            >
              {saving === 'centre_comment' ? 'Saving…' : 'Save'}
            </Button>
          </styles.CommentFoot>
        </styles.Cell>

        <styles.Cell>
          <TextField
            multiline
            minRows={2}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="HQ reply (admins only)"
            value={hqDraft}
            onChange={(e) => setHqDraft(e.target.value)}
          />
          <styles.CommentFoot>
            {row.hq_comment_by_name ? (
              <span>
                {row.hq_comment_by_name}
                {row.hq_comment_at
                  ? ` · ${dayjs(row.hq_comment_at).format('D MMM, HH:mm')}`
                  : ''}
              </span>
            ) : (
              <span />
            )}
            <Button
              size="small"
              disabled={!hqDirty || saving === 'hq_comment'}
              onClick={() => save('hq_comment', hqDraft)}
            >
              {saving === 'hq_comment' ? 'Saving…' : 'Save'}
            </Button>
          </styles.CommentFoot>
          <styles.ReviewLabel>Review</styles.ReviewLabel>
          <Select
            variant="outlined"
            margin="dense"
            fullWidth
            value={row.review_status}
            onChange={(e) => save('review_status', e.target.value)}
          >
            {REVIEW_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </styles.Cell>
      </styles.Grid>

      <Collapse in={open} unmountOnExit>
        <styles.Lines>
          <styles.LineHead>
            <span>Child</span>
            <span>Category</span>
            <span>Centre</span>
            <span>Before</span>
            <span>Discount</span>
            <span>Final</span>
            <span>Coins</span>
            <span>Shortfall</span>
          </styles.LineHead>
          {(row.lines || []).map((l) => (
            <styles.Line key={l.uuid}>
              <span>{l.student_name}</span>
              <span>
                {l.subject}
                {l.is_paused ? ' (paused)' : ''}
                {l.already_booked ? ' — already booked' : ''}
              </span>
              <span>{l.batch_centre}</span>
              <span>{rupees(l.cost_before_discount)}</span>
              <span>−{rupees(l.discount_value)}</span>
              <span>{rupees(l.final_cost)}</span>
              <span>{Number(l.coins_used || 0).toLocaleString('en-IN')}</span>
              <span>{rupees(l.shortfall)}</span>
            </styles.Line>
          ))}
        </styles.Lines>
      </Collapse>
    </styles.Row>
  )
}
